import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import ContractorReferralAdminApi from "../../../../../infrastructure/api/contractorReferralAdmin";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import { formatApiDate } from "../../market-growth/utils/commissionValidation";
import {
  getAttributionStatusLabel,
  REFERRAL_ATTRIBUTION_STATUS_LABELS,
} from "../../market-growth/constants/reportEnums";

const PAGE_BG = "#F6F8FB";
const PAGE_SIZE = 20;

const formatDate = (value) =>
  value ? new Date(value).toLocaleString("fa-IR") : "—";

const STATUS_OPTIONS = Object.entries(REFERRAL_ATTRIBUTION_STATUS_LABELS).map(
  ([value, label]) => ({ value, label })
);

const SummaryCards = ({ summary }) => {
  const s = summary || {};
  const items = [
    { label: "کل رهگیری‌ها", value: s.totalVisits ?? s.TotalVisits ?? 0 },
    { label: "ثبت‌نام‌شده", value: s.registeredCount ?? s.RegisteredCount ?? 0 },
    {
      label: "دارای درخواست پروژه",
      value: s.projectCreatedCount ?? s.ProjectCreatedCount ?? 0,
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {items.map((item) => (
        <Grid item xs={12} sm={4} key={item.label}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">
                {item.label}
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {item.value}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

const ManagementContractorReferralReportPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    referralCode: "",
    contractorInfoId: "",
    registeredUserId: "",
    relatedProjectId: "",
    status: "",
    fromDate: null,
    toDate: null,
  });

  const loadContractors = useCallback(async () => {
    const res = await ContractorReferralAdminApi.getContractorOptions();
    const { error: err, data } = ContractorReferralAdminApi.normalizeContractors(res);
    if (!err) {
      setContractors(Array.isArray(data) ? data : []);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: page + 1, pageSize: PAGE_SIZE };
      if (filters.referralCode.trim()) params.referralCode = filters.referralCode.trim();
      if (filters.contractorInfoId) params.contractorInfoId = Number(filters.contractorInfoId);
      if (filters.registeredUserId) params.registeredUserId = Number(filters.registeredUserId);
      if (filters.relatedProjectId) params.relatedProjectId = Number(filters.relatedProjectId);
      if (filters.status) params.status = filters.status;
      if (filters.fromDate) params.fromDate = formatApiDate(filters.fromDate);
      if (filters.toDate) params.toDate = formatApiDate(filters.toDate);

      const res = await ContractorReferralAdminApi.getAttributions(params);
      const { error: err, data } = ContractorReferralAdminApi.normalizeAttributions(res);
      if (err) {
        setError(err);
        setPageData(null);
        return;
      }
      setPageData(data);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadContractors();
  }, [loadContractors]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const items = pageData?.items ?? pageData?.Items ?? [];
    return Array.isArray(items) ? items : [];
  }, [pageData]);

  const totalCount = Number(pageData?.totalCount ?? pageData?.TotalCount ?? 0);
  const summary = pageData?.summary ?? pageData?.Summary ?? null;

  const contractorLabelMap = useMemo(
    () =>
      contractors.reduce((acc, c) => {
        const id = c.id ?? c.Id;
        const name = c.companyName ?? c.CompanyName ?? `پیمانکار ${id}`;
        const code = c.referralCode ?? c.ReferralCode ?? "";
        acc[id] = code ? `${name} (${code})` : name;
        return acc;
      }, {}),
    [contractors]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, background: PAGE_BG, minHeight: "100%" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            گزارش معرفی پیمانکاران
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            رهگیری لینک‌های معرفی پیمانکار، ثبت‌نام متقاضیان و وضعیت پروژه‌های
            ارجاع‌شده.
          </Typography>
        </Box>
        <Button startIcon={<SyncIcon />} variant="outlined" onClick={load} disabled={loading}>
          بروزرسانی
        </Button>
      </Stack>

      <SummaryCards summary={summary} />

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="کد معرف"
                value={filters.referralCode}
                onChange={(e) => {
                  setPage(0);
                  setFilters((prev) => ({ ...prev, referralCode: e.target.value }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>پیمانکار</InputLabel>
                <Select
                  label="پیمانکار"
                  value={filters.contractorInfoId}
                  onChange={(e) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, contractorInfoId: e.target.value }));
                  }}
                >
                  <MenuItem value="">همه</MenuItem>
                  {contractors.map((c) => {
                    const id = c.id ?? c.Id;
                    return (
                      <MenuItem key={id} value={String(id)}>
                        {contractorLabelMap[id]}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="شناسه کاربر"
                value={filters.registeredUserId}
                onChange={(e) => {
                  setPage(0);
                  setFilters((prev) => ({ ...prev, registeredUserId: e.target.value }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="شناسه پروژه"
                value={filters.relatedProjectId}
                onChange={(e) => {
                  setPage(0);
                  setFilters((prev) => ({ ...prev, relatedProjectId: e.target.value }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت</InputLabel>
                <Select
                  label="وضعیت"
                  value={filters.status}
                  onChange={(e) => {
                    setPage(0);
                    setFilters((prev) => ({ ...prev, status: e.target.value }));
                  }}
                >
                  <MenuItem value="">همه</MenuItem>
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <JalaliDatePicker
                label="از تاریخ"
                value={filters.fromDate}
                onChange={(value) => {
                  setPage(0);
                  setFilters((prev) => ({ ...prev, fromDate: value }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <JalaliDatePicker
                label="تا تاریخ"
                value={filters.toDate}
                onChange={(value) => {
                  setPage(0);
                  setFilters((prev) => ({ ...prev, toDate: value }));
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>پیمانکار</TableCell>
                    <TableCell>کد معرف</TableCell>
                    <TableCell>وضعیت</TableCell>
                    <TableCell>متقاضی</TableCell>
                    <TableCell>موبایل</TableCell>
                    <TableCell>کد پروژه</TableCell>
                    <TableCell>مرحله پروژه</TableCell>
                    <TableCell>اولین بازدید</TableCell>
                    <TableCell>ثبت‌نام</TableCell>
                    <TableCell>ثبت درخواست</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          موردی یافت نشد.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const status = row.status ?? row.Status;
                      const contractorId = row.contractorInfoId ?? row.ContractorInfoId;
                      return (
                        <TableRow key={row.id ?? row.Id} hover>
                          <TableCell>
                            {row.contractorCompanyName ??
                              row.ContractorCompanyName ??
                              contractorLabelMap[contractorId] ??
                              "—"}
                          </TableCell>
                          <TableCell>{row.referralCode ?? row.ReferralCode ?? "—"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={getAttributionStatusLabel(status)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {row.registeredUserDisplayName ??
                              row.RegisteredUserDisplayName ??
                              "—"}
                          </TableCell>
                          <TableCell>
                            {row.registeredUserMobile ??
                              row.RegisteredUserMobile ??
                              row.maskedRegisteredUserMobile ??
                              row.MaskedRegisteredUserMobile ??
                              "—"}
                          </TableCell>
                          <TableCell>{row.projectCode ?? row.ProjectCode ?? "—"}</TableCell>
                          <TableCell>
                            {row.projectStepTitle ?? row.ProjectStepTitle ?? "—"}
                          </TableCell>
                          <TableCell>{formatDate(row.firstSeenAt ?? row.FirstSeenAt)}</TableCell>
                          <TableCell>{formatDate(row.registeredAt ?? row.RegisteredAt)}</TableCell>
                          <TableCell>
                            {formatDate(row.projectCreatedAt ?? row.ProjectCreatedAt)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, next) => setPage(next)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} از ${count !== -1 ? count : `بیش از ${to}`}`
                }
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ManagementContractorReferralReportPage;
